// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"noodle-frontend@80.18.2"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "14.2.17";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/noodle-frontend/qa";globalThis["_sentryAssetPrefix"] = "https://cdn.qa.glade.ai/noodle-frontend/qa";globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';

import getRelease from './scripts/getRelease';
import getFingerprint from './sentry/getFingerprint';
import ignoreEvent from './sentry/ignoreEvent';

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

const environment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
const debug = process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true';
const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
const enabled = process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true';
const enableLogs = process.env.NEXT_PUBLIC_SENTRY_LOGGING_ENABLED === 'true';
const replayEnabled = process.env.NEXT_PUBLIC_SENTRY_REPLAY_ENABLED === 'true';
const release = getRelease();

type FeatureFlags = Record<string, boolean | string | null | undefined>;

let cachedFlags: FeatureFlags | null = null;
let cachedAt = 0;
const CACHE_MS = 10_000;

function getCachedPosthogFlags(): FeatureFlags {
  const ph = posthog;
  const featureFlagsApi = ph?.featureFlags;
  const getFlags = featureFlagsApi?.getFlags?.bind(featureFlagsApi);
  const getFlagValue = ph?.getFeatureFlag?.bind(ph);
  const now = Date.now();

  if (cachedFlags && now - cachedAt < CACHE_MS) return cachedFlags;

  // Limit to avoid bloating Sentry payloads
  const keys = Array.isArray(getFlags?.()) ? getFlags().slice(0, 50) : [];

  cachedFlags = Object.fromEntries(keys.map((k) => [k, getFlagValue ? (getFlagValue(k) ?? null) : null]));
  cachedAt = now;

  return cachedFlags;
}

Sentry.init({
  beforeSend(event, hint) {
    const error = hint?.originalException;
    if (ignoreEvent({ error })) {
      return null;
    }

    const finalEvent = { ...event };
    if (event.logger === 'console' && event.message) {
      finalEvent.message = event.message.replace(/\[object Object\]\s*/g, '');
    }

    const fingerprint = getFingerprint({ error, event });
    if (fingerprint) {
      finalEvent.fingerprint = fingerprint;
    }

    // Add PostHog feature flags as Sentry tags so they are searchable
    try {
      const flags = getCachedPosthogFlags();
      const flagTags = Object.fromEntries(
        Object.entries(flags).map(([key, value]) => [`postHogFeatureFlag.${key}`, value]),
      );

      finalEvent.tags = {
        ...finalEvent.tags,
        ...flagTags,
      };
    } catch {
      // Non-fatal: never block Sentry on feature flag enrichment
    }

    return finalEvent;
  },
  debug,
  dsn,
  enableLogs,
  enabled,
  environment,
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
  // but not "Non-Error exception captured" because we were throwing objects for a while.
  ignoreErrors: [
    'Non-Error promise rejection captured with value: undefined',
    'Non-Error promise rejection captured with value: Object Not Found Matching Id:',
    'Could not verify one time passcode.',
    'Could not send one time passcode.',
    'Mixpanel error: Bad HTTP status: 0',

    // ignore for now, not prioritizing fixing and just cluttering sentry.
    'The user aborted a request',
    'Network Error',
    'Cancel rendering route',
    'Loading initial props cancelled',
    /^Route did not complete loading/,
    'Inital load had uninitialized params',
    /^Abort fetching component for route/,
    'Failed to load static props',
    'Load failed',
    'ResizeObserver loop completed with undelivered notifications.',
    /^Failed to fetch/,
    "Failed to register a ServiceWorker: The URL protocol of the current origin ('null') is not supported.",
  ],
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['error'],
    }),
    ...(replayEnabled ? [Sentry.replayIntegration()] : []),
  ],
  release: release?.name,
  // Don't take random sessions, only when explicitly enabled with useSentryReplay
  replaysOnErrorSampleRate: 0.0,
  replaysSessionSampleRate: 0.0,
  tracesSampler: (samplingContext) => (samplingContext.sampleAll ? 1 : 0.05),
});
